// apps/nos/client/src/pages/CreateFlavorPage/steps/ChromatogramMode.tsx
//
// "Add new substance" sub-mode: name the substance + upload chromatograms.
// The name field (with its debounced duplicate-name check) lives here; the
// file-upload surface is a SEPARATE component (ChromatogramFiles) that owns the
// live upload-progress state locally, so frequent progress ticks never re-render
// this name input (which previously dropped keystrokes during an upload).

import { useEffect, useRef, useState } from "react";
import { useAuthHeaders } from "@/services/uploads_api";
import { checkFoodName } from "@/services/food_api";
import type { Chroma } from "@/utils/types/upload";
import { ChromatogramFiles, RequiredStar } from "./ChromatogramFiles";

const NAME_CHECK_DEBOUNCE_MS = 350;

// Re-export for backwards-compat with imports elsewhere.
export type { Chroma };

type ChromatogramModeProps = {
  draftId: string;
  referenceName: string;
  onReferenceNameChange: (value: string) => void;
  chromas: Chroma[];
  onChromasChange: (next: Chroma[]) => void;
  /** Per-file experiment-config assignment (fileUploadId → user_exp_config_id),
   *  owned by the parent anchor so the submit can forward it as `configs`. */
  chromaConfigById: Record<string, string>;
  onChromaConfigByIdChange: (next: Record<string, string>) => void;
  onDuplicateNameChange?: (isDuplicate: boolean) => void;
  /** Override the name-field label/placeholder (the "Add new substance" sub-mode
   *  reuses this component but frames it as naming a substance). */
  nameLabel?: string;
  namePlaceholder?: string;
};

export function ChromatogramMode({
  draftId,
  referenceName,
  onReferenceNameChange,
  chromas,
  onChromasChange,
  chromaConfigById,
  onChromaConfigByIdChange,
  onDuplicateNameChange,
  nameLabel = "Name this target reference",
  namePlaceholder = "e.g. Alphonso mango pulp · 2024 batch",
}: ChromatogramModeProps) {
  const getHeaders = useAuthHeaders();

  // Debounced duplicate-name check: hits /me/food/check-name and stores the
  // exact trimmed name that returned exists=true. As soon as the user edits
  // (referenceName.trim() !== dupeForName), the duplicate notice clears
  // automatically without waiting for the next request.
  const getHeadersRef = useRef(getHeaders);
  useEffect(() => {
    getHeadersRef.current = getHeaders;
  }, [getHeaders]);
  const [dupeForName, setDupeForName] = useState<string | null>(null);
  useEffect(() => {
    const trimmed = referenceName.trim();
    if (trimmed.length === 0) {
      setDupeForName(null);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const headers = await getHeadersRef.current();
        const r = await checkFoodName(trimmed, headers, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setDupeForName(r.exists ? r.name : null);
      } catch {
        // Network error or aborted — leave the previous state.
      }
    }, NAME_CHECK_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [referenceName]);

  const showDuplicate = dupeForName !== null && referenceName.trim() === dupeForName;

  // Notify parent so it can gate "Start designing". Cleanup resets to false on
  // unmount (e.g. anchorMode switched to catalog).
  useEffect(() => {
    onDuplicateNameChange?.(showDuplicate);
    return () => {
      onDuplicateNameChange?.(false);
    };
  }, [showDuplicate, onDuplicateNameChange]);

  return (
    <div className="vstack gap-3">
      {/* Reference name (required) — isolated from upload-progress re-renders */}
      <div className="vstack gap-2">
        <label className="hstack" style={{ alignItems: "baseline" }}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>
            {nameLabel}
            <RequiredStar />
          </span>
        </label>
        <input
          type="text"
          value={referenceName}
          onChange={(e) => onReferenceNameChange(e.target.value)}
          placeholder={namePlaceholder}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 6,
            border: showDuplicate ? "1px solid var(--err, #c33)" : "1px solid var(--line)",
            background: "var(--bg)",
            fontSize: 13,
            outline: "none",
          }}
        />
        {showDuplicate ? (
          <span style={{ fontSize: 11, color: "var(--err, #c33)", lineHeight: 1.5 }}>
            A target reference named &ldquo;{dupeForName}&rdquo; already exists in your
            organization. Pick a different name.
          </span>
        ) : (
          <span style={{ fontSize: 11, color: "var(--ink-4)", lineHeight: 1.5 }}>
            We'll save it under this name so you can refer back, compare, or reuse it on later
            formulations.
          </span>
        )}
      </div>

      <ChromatogramFiles
        draftId={draftId}
        initialChromas={chromas}
        onChromasChange={onChromasChange}
        chromaConfigById={chromaConfigById}
        onChromaConfigByIdChange={onChromaConfigByIdChange}
      />
    </div>
  );
}
